<template>
    <v-card>
        <v-card-title v-if="!alertMode">
            <v-btn small icon @click="close" color="primary" class="ml-n3 mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h2 class="font-weight-regular">
                Assembly Items
            </h2>
        </v-card-title>
        <v-divider class="mx-6" />
        <v-card-text>
            <v-form v-model="valid">
                <v-alert
                    v-if="alertMode && assemblyTableReadOnly"
                    type="error"
                    dense
                    outlined
                    class="my-3"
                >
                    Assembly line items are not editable because production has
                    already been recorded for this part number.
                </v-alert>
                <v-data-table
                    :headers="headers"
                    :items="filteredItems"
                    class="elevation-0 mt-3"
                    :class="{
                        'assembly-form-table--readonly': assemblyTableReadOnly,
                    }"
                    :style="
                        alertMode ? 'margin-top: -10px;' : 'margin-top: 7px;'
                    "
                    :mobile-breakpoint="0"
                    :loading="loading"
                    :disable-pagination="!alertMode"
                    :hide-default-footer="!alertMode"
                    :items-per-page="alertMode ? 25 : -1"
                    :footer-props="
                        alertMode
                            ? {
                                  itemsPerPageOptions: [25, 50, 100],
                                  itemsPerPageText: 'Items Per Page',
                              }
                            : undefined
                    "
                    :height="$vuetify.breakpoint.mdAndDown ? '50vh' : '54vh'"
                    fixed-header
                >
                    <template v-slot:top v-if="!alertMode">
                        <v-row
                            no-gutter
                            class="mt-0 mx-0 pt-4 pb-0 pb-4"
                            style="background-color: #eeeeee"
                        >
                            <v-col cols="6" class="d-flex align-center">
                                <h2 class="my-n3 d-flex align-center">
                                    ITEMS
                                </h2></v-col
                            >
                        </v-row>
                    </template>
                    <template v-slot:[`header.code`]="{ header }">
                        <v-text-field
                            :label="header.text"
                            v-model="codeToFilter"
                            dense
                            class="pt-2"
                            :disabled="assemblyTableReadOnly"
                            @input="verifyCheckBox"
                        />
                    </template>
                    <!--ITEMS-->
                    <template v-slot:[`item.index`]="{ index }">
                        <p class="my-0">
                            {{ index + 1 }}
                        </p>
                    </template>
                    <template v-slot:[`item.available`]="{ item }">
                        <div class="d-flex justify-center">
                            <p class="my-0">
                                {{ item.available || 0 }}
                            </p>
                        </div>
                    </template>
                    <template v-slot:[`item.qtyPerAssembly`]="{ item }">
                        <div class="mb-2">
                            <v-text-field
                                v-model="item.qtyPerAssembly"
                                type="number"
                                :disabled="assemblyTableReadOnly"
                                :rules="[() => maxValue(item)]"
                                @input="verifyCheckBox"
                            />
                        </div>
                    </template>
                    <template v-slot:[`item.totalQty`]="{ item }">
                        <div class="d-flex justify-center">
                            <p class="my-0">
                                {{
                                    (+item.qtyPerAssembly || 0) *
                                        (+parentItem.quantity || 0)
                                }}
                            </p>
                        </div>
                    </template>
                </v-data-table>
                <PartNumberAttachments
                    v-if="alertMode"
                    v-model="parentItemFiles"
                    :partNumberCode="assemblyParentPartCode"
                    :workOrder="workOrder"
                />
            </v-form>
        </v-card-text>
        <v-card-actions v-if="!alertMode">
            <v-row
                v-if="!codeToFilter"
                class="ml-1 d-flex justify-left align-center"
            >
                <v-simple-checkbox
                    color="primary"
                    v-model="setAvailable"
                    @click="setAllAvailable"
                    class="ml-2 d-flex justify-center align center"
                >
                </v-simple-checkbox>
                <small>Select All Available Quantities</small>
            </v-row>
            <v-row v-else class="ml-1 d-flex justify-left align-center">
                <v-simple-checkbox
                    color="primary"
                    v-model="setAvailableInFiltered"
                    @click="setAllAvailableInFiltered"
                    class="ml-2 d-flex justify-center align center"
                >
                </v-simple-checkbox>
                <small>Set Available Quantities In Filtered Items</small>
            </v-row>

            <v-spacer />
            <v-btn
                :loading="loading"
                color="primary"
                rounded
                class="mb-3 mr-3"
                :disabled="disableButton()"
                @click="openConfirmDialog"
            >
                NEXT
            </v-btn>
        </v-card-actions>
        <v-dialog
            :retain-focus="false"
            v-model="confirmDialog"
            persistent
            max-width="800px"
        >
            <v-card>
                <v-card-title>
                    <v-btn
                        small
                        icon
                        @click="closeConfirmDialog"
                        color="primary"
                        class="ml-n3 mr-2"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <h2 class="font-weight-regular">
                        Confirm Assembly Items
                    </h2>
                </v-card-title>
                <v-divider class="mx-6" />
                <v-card-text>
                    <v-form v-model="valid">
                        <v-data-table
                            :headers="confirmHeaders"
                            :items="
                                items.filter(
                                    i =>
                                        i.qtyPerAssembly && i.qtyPerAssembly > 0
                                )
                            "
                            class="elevation-0 mt-7"
                            :mobile-breakpoint="0"
                            :loading="loading"
                            disable-pagination
                            hide-default-footer
                            :height="
                                $vuetify.breakpoint.mdAndDown ? '50vh' : '54vh'
                            "
                            fixed-header
                        >
                            <template v-slot:top>
                                <v-row
                                    no-gutter
                                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                                    style="background-color: #eeeeee"
                                >
                                    <v-col
                                        cols="12"
                                        class="d-flex align-center"
                                    >
                                        <h2 class="my-n3 d-flex align-center">
                                            ITEMS
                                        </h2></v-col
                                    >
                                </v-row>
                            </template>
                            <!--ITEMS-->
                            <template v-slot:[`item.index`]="{ index }">
                                <p class="my-0">
                                    {{ index + 1 }}
                                </p>
                            </template>
                            <template v-slot:[`item.totalQty`]="{ item }">
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{
                                            (+item.qtyPerAssembly || 0) *
                                                (+parentItem.quantity || 0)
                                        }}
                                    </p>
                                </div>
                            </template>
                        </v-data-table>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pt-0 pb-5">
                    <v-row class="ma-2">
                        <v-spacer />
                        <v-col cols="3" class="d-flex justify-end pa-0 ">
                            <v-btn
                                color="primary"
                                rounded
                                :loading="loading"
                                @click="save"
                            >
                                SAVE
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'
import PartNumberAttachments from '@/components/WorkOrders/PartNumberAttachments.vue'

export default {
    name: 'AssemblyForm',
    components: {
        PartNumberAttachments,
    },
    props: {
        workOrder: {
            type: Object,
            required: true,
        },
        parentItem: {
            type: Object,
            required: true,
        },
        originalItems: {
            type: Array,
            required: true,
        },
        alertMode: {
            type: Boolean,
            default: false,
        },
        assemblyTableReadOnly: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        valid: false,
        loading: false,
        headers: [
            {
                text: 'ID',
                value: 'index',
                align: 'center',
                sortable: false,
                width: '50',
            },
            {
                text: 'PART NUMBER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'AVAILABLE QTY',
                value: 'available',
                align: 'center',
                sortable: false,
                width: '130',
            },
            {
                text: 'QTY PER ASSEMBLY',
                value: 'qtyPerAssembly',
                align: 'center',
                sortable: false,
                width: 150,
            },
            {
                text: 'TOTAL QTY',
                value: 'totalQty',
                align: 'center',
                sortable: false,
                width: 130,
            },
        ],
        confirmHeaders: [
            {
                text: 'ID',
                value: 'index',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PART NUMBER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'QUANTITY PER ASSEMBLY',
                value: 'qtyPerAssembly',
                align: 'center',
                sortable: false,
            },
            {
                text: 'TOTAL QUANTITY',
                value: 'totalQty',
                align: 'center',
                sortable: false,
            },
        ],
        rules: {
            required: v => !!v || 'The value is required',
        },

        confirmDialog: false,
        codeToFilter: undefined,
        setAvailable: false,
        setAvailableInFiltered: false,
        items: [],
        partNumbersProductionData: [],
    }),
    computed: {
        filteredItems() {
            let conditions = []
            conditions.push(this.filterByAvailable)
            if (this.codeToFilter) {
                conditions.push(this.filterForPartNumber)
            }
            if (conditions.length > 0) {
                return this.items.filter(item => {
                    return conditions.every(condition => {
                        return condition(item)
                    })
                })
            }

            return this.items
        },
        assemblyParentPartCode() {
            const p = this.parentItem
            if (!p) return ''
            if (typeof p.partNumber === 'object' && p.partNumber !== null) {
                return String(p.partNumber.code ?? p.partNumber.id ?? '').trim()
            }
            if (p.partNumber != null && p.partNumber !== '') {
                return String(p.partNumber).trim()
            }
            return ''
        },
        parentItemFiles: {
            get() {
                return this.parentItem.files || []
            },
            set(files) {
                this.$set(
                    this.parentItem,
                    'files',
                    Array.isArray(files) ? [...files] : []
                )
            },
        },
    },
    mounted() {
        if (this.alertMode && !this.parentItem.files) {
            this.$set(this.parentItem, 'files', [])
        }
        if (this.alertMode) {
            this.parentItem.assemblyItems = Object.values(
                this.parentItem.assemblyItems
            )
        }
        this.items = _.cloneDeep(this.originalItems)
        if (this.parentItem.assemblyItems) {
            this.parentItem.assemblyItems.forEach(ai => {
                const itemIndex = this.items.findIndex(
                    i => i.code == ai.partNumber
                )
                if (itemIndex > -1) {
                    this.items[itemIndex].available +=
                        (ai.qtyPerAssembly || 0) * this.parentItem.quantity
                    this.items[itemIndex].qtyPerAssembly = ai.qtyPerAssembly
                }
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async save() {
            try {
                this.loading = true
                let filteredItems = this.items.filter(
                    i => i.qtyPerAssembly && i.qtyPerAssembly > 0
                )

                let assemblyItems = filteredItems.map(fi => ({
                    partNumber: fi.code,
                    partNumberId: fi.id,
                    qtyPerAssembly: fi.qtyPerAssembly,
                }))

                if (this.alertMode) {
                    const updates = this.partNumberUpdates(assemblyItems)
                    this.$emit('updatePartNumbers', updates)
                } else {
                    await API.saveAssemblyItems({
                        items: assemblyItems,
                        workOrderId: this.workOrder.id,
                        partNumberId: this.parentItem.partNumberId,
                    })
                    const updates = this.partNumberUpdates(assemblyItems)
                    this.$emit('updatePartNumbers', updates)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },

        openConfirmDialog() {
            this.confirmDialog = true
        },

        closeConfirmDialog() {
            this.confirmDialog = false
        },

        close() {
            this.$emit('closeDialog')
        },

        disableButton() {
            const selectedItems = this.items.filter(
                item => item.qtyPerAssembly > 0
            )
            const completeForm = selectedItems.every(
                i => i.qtyPerAssembly * this.parentItem.quantity <= i.available
            )
            return selectedItems.length == 0 || !completeForm
        },

        filterForPartNumber(item) {
            return (
                item.code &&
                item.code
                    .toLowerCase()
                    .includes(this.codeToFilter.toLowerCase())
            )
        },

        //! GUIA
        maxValue(item) {
            const totalValue = +item.qtyPerAssembly * +this.parentItem.quantity

            if (totalValue > item.available) {
                return `The quantity selected cannot be greater than ${Math.floor(
                    +item.available / +this.parentItem.quantity
                )}`
            } else if (item.qtyPerAssembly < 0) {
                return 'The value must be positive'
            } else {
                return true
            }
        },
        setAllAvailable() {
            this.items.forEach(item => {
                if (this.setAvailable && item.available && item.available > 0) {
                    item.qtyPerAssembly = Math.floor(
                        Number(item.available / this.parentItem.quantity)
                    )
                } else {
                    delete item.qtyPerAssembly
                }
            })
        },
        verifyCheckBox() {
            if (this.assemblyTableReadOnly) return
            this.setAvailable = !this.items.some(
                item =>
                    (item.qtyPerAssembly || 0) <
                    Math.floor(
                        Number(item.available / this.parentItem.quantity)
                    )
            )
            this.setAvailableInFiltered = !this.filteredItems.some(
                item =>
                    (item.qtyPerAssembly || 0) <
                    Math.floor(
                        Number(item.available / this.parentItem.quantity)
                    )
            )
        },
        setAllAvailableInFiltered() {
            this.items.forEach(item => {
                if (
                    this.setAvailableInFiltered &&
                    item.available &&
                    item.available > 0 &&
                    this.filteredItems.includes(item)
                ) {
                    item.qtyPerAssembly = Math.floor(
                        Number(item.available / this.parentItem.quantity)
                    )
                } else if (
                    !this.setAvailableInFiltered &&
                    this.filteredItems.includes(item)
                ) {
                    delete item.qtyPerAssembly
                }
            })
        },
        filterByAvailable(item) {
            return (
                +item.available > 0 ||
                (this.parentItem &&
                    this.parentItem.assemblyItems &&
                    this.parentItem.assemblyItems
                        .map(i => i.partNumber)
                        .includes(item.code))
            )
        },

        sort() {
            return this.items.sort(
                (a, b) => Number(b.available) - Number(a.available)
            )
        },

        partNumberUpdates(assemblyItems) {
            const updates = []
            const parentQty = +this.parentItem.quantity
            const updatedItemsMap = new Map()
            for (const item of assemblyItems) {
                updatedItemsMap.set(item.partNumber, item)
            }
            for (const originalAssemblyItem of this.parentItem.assemblyItems ||
                []) {
                const currentItemAssembly = updatedItemsMap.get(
                    originalAssemblyItem.partNumber
                )

                if (currentItemAssembly) {
                    if (
                        +originalAssemblyItem.qtyPerAssembly !==
                        +currentItemAssembly.qtyPerAssembly
                    ) {
                        updates.push({
                            partNumber: originalAssemblyItem.partNumber,
                            updatedQty:
                                (+currentItemAssembly.qtyPerAssembly -
                                    +originalAssemblyItem.qtyPerAssembly) *
                                parentQty,
                        })
                    }
                    updatedItemsMap.delete(originalAssemblyItem.partNumber)
                } else {
                    updates.push({
                        partNumber: originalAssemblyItem.partNumber,
                        updatedQty:
                            -originalAssemblyItem.qtyPerAssembly * parentQty,
                    })
                }
            }

            for (const [partNumber, item] of updatedItemsMap) {
                updates.push({
                    partNumber,
                    updatedQty: +item.qtyPerAssembly * parentQty,
                })
            }
            return updates
        },
    },
}
</script>

<style scoped>
.assembly-form-table--readonly >>> tbody .v-input__slot {
    opacity: 0.85;
}
</style>
